import { CheckCircle, Clock, Copy, QrCode, ShieldCheck, Smartphone, XCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import type { PixPayment } from "@melhot/shared";
import { formatMoney } from "@melhot/shared/money";

function normalizeQrImage(value?: string) {
  if (!value) return undefined;
  if (value.startsWith("data:image/") || value.startsWith("http://") || value.startsWith("https://")) return value;
  if (/^[A-Za-z0-9+/]+=*$/.test(value) && value.length > 120) return `data:image/png;base64,${value}`;
  return undefined;
}

export function PixQrCode({ payment }: { payment: PixPayment }) {
  const providedImage = useMemo(() => normalizeQrImage(payment.pixQrCodeBase64 ?? payment.pixQrCode), [payment.pixQrCodeBase64, payment.pixQrCode]);
  const [generatedImage, setGeneratedImage] = useState<string | undefined>();

  useEffect(() => {
    let active = true;
    setGeneratedImage(undefined);
    if (providedImage || !payment.pixCopyPaste) return;
    QRCode.toDataURL(payment.pixCopyPaste, { margin: 1, width: 300, errorCorrectionLevel: "M", color: { dark: "#111827", light: "#ffffff" } })
      .then((dataUrl) => {
        if (active) setGeneratedImage(dataUrl);
      })
      .catch(() => {
        if (active) setGeneratedImage(undefined);
      });
    return () => {
      active = false;
    };
  }, [payment.pixCopyPaste, providedImage]);

  const image = providedImage ?? generatedImage;

  if (!image) {
    return (
      <div className="mx-auto grid h-72 w-72 max-w-full place-items-center border border-line bg-white p-4 text-center text-sm text-slate-500 sm:h-60 sm:w-60">
        <span>
          <QrCode className="mx-auto mb-2 text-trust" />
          Use o código copia e cola abaixo.
        </span>
      </div>
    );
  }

  return <img src={image} alt="QR Code PIX" className="mx-auto h-72 w-72 max-w-full border border-line bg-white p-3 sm:h-60 sm:w-60" />;
}

export function PixCopyPaste({ code }: { code?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="border border-line bg-white p-3">
      <p className="break-all text-xs text-slate-600">{code}</p>
      <button
        type="button"
        onClick={async () => {
          if (!code) return;
          await navigator.clipboard.writeText(code);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 2500);
        }}
        className="mx-auto mt-3 flex w-full items-center justify-center gap-2 bg-trust px-3 py-3 text-sm font-semibold text-white shadow-lg shadow-trust/20 sm:w-fit sm:py-2.5 sm:shadow-none"
      >
        <Copy size={16} /> {copied ? "Código copiado" : "Copiar código PIX"}
      </button>
    </div>
  );
}

export function PixStatus({ status }: { status: PixPayment["status"] }) {
  const icon = status === "PAID" ? <CheckCircle /> : status === "EXPIRED" || status === "FAILED" ? <XCircle /> : <Clock />;
  const text = status === "PAID" ? "Pagamento confirmado" : status === "EXPIRED" ? "PIX expirado" : status === "FAILED" ? "Erro no PIX" : "Aguardando pagamento";
  return (
    <div className="flex items-center gap-2 text-sm font-semibold text-ink">
      {icon}
      {text}
    </div>
  );
}

export function PixPaymentSection({ payment, secondsLeft }: { payment: PixPayment; secondsLeft: number }) {
  return (
    <section className="border border-slate-200 bg-white p-4 sm:p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Pagamento PIX</h1>
          <p className="text-sm text-slate-600">Valor {formatMoney(payment.amount)}</p>
        </div>
        <PixStatus status={payment.status} />
      </div>
      <PixQrCode payment={payment} />
      <p className="my-4 text-center text-sm text-slate-600">
        Expira em {Math.max(0, Math.floor(secondsLeft / 60))}:{String(Math.max(0, secondsLeft % 60)).padStart(2, "0")}
      </p>
      <PixCopyPaste code={payment.pixCopyPaste} />
      <div className="mt-4 border border-line bg-soft p-4">
        <h2 className="font-semibold text-ink">Como pagar</h2>
        <ol className="mt-3 space-y-2 text-sm text-slate-700">
          <li className="flex gap-2">
            <Smartphone size={17} className="mt-0.5 shrink-0 text-trust" /> Abra o app do seu banco e escolha a opção PIX.
          </li>
          <li className="flex gap-2">
            <Copy size={17} className="mt-0.5 shrink-0 text-trust" /> Escaneie o QR Code ou cole o código copiado.
          </li>
          <li className="flex gap-2">
            <ShieldCheck size={17} className="mt-0.5 shrink-0 text-trust" /> Confirme o pagamento no banco e aguarde a confirmação automática aqui.
          </li>
        </ol>
      </div>
      <p className="mt-4 text-center text-xs text-slate-500">
        Não feche esta página enquanto o pagamento estiver pendente. Se o banco confirmar, nosso sistema atualiza o pedido e envia você para a página de obrigado.
      </p>
    </section>
  );
}
